import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LiteratureExpertIcon from "@mui/icons-material/LocalLibrary";
import type { SelectChangeEvent } from "@mui/material";
import { useCallback, useMemo } from "react";
import { FormattedMessage } from "react-intl";

import { PredictorIcon } from "@citrine/atoms/icons";
import type { SelectOption } from "@citrine/atoms/select";

import type { CatalystMode } from "#src/contexts/catalyst";

import * as sx from "./mode-selector.sx";
import { TEST_IDS } from "./utils";

type ModeOption = SelectOption<CatalystMode>;

const OPTIONS: ModeOption[] = [
  {
    label: <FormattedMessage id="catalyst.literature-expert.abbreviation" />,
    value: "literature-expert",
  },
];

export function ModeSelector({
  agentEnabled,
  disabled,
  modelExpertEnabled,
  onChange,
  value,
}: {
  agentEnabled?: boolean;
  disabled?: boolean;
  modelExpertEnabled?: boolean;
  onChange: (value: CatalystMode) => void;
  value: CatalystMode;
}) {
  const options = useMemo<ModeOption[]>(
    () =>
      OPTIONS.concat(
        [
          modelExpertEnabled
            ? {
                label: (
                  <FormattedMessage id="catalyst.model-expert.abbreviation" />
                ),
                value: "model-expert" as const,
              }
            : null,
          agentEnabled
            ? {
                label: <FormattedMessage id="catalyst.agent" />,
                value: "agent" as const,
              }
            : null,
        ].filter(Boolean),
      ),
    [agentEnabled, modelExpertEnabled],
  );

  const onSelection = useCallback(
    (event: SelectChangeEvent<CatalystMode>) => {
      onChange(event.target.value);
    },
    [onChange],
  );

  return (
    <sx.Select
      data-fullstoryid="catalyst.mode-selector"
      data-testid={TEST_IDS.modeSelector}
      disabled={disabled}
      onChange={onSelection}
      options={options}
      startAdornment={
        value === "agent" ? (
          <AutoAwesomeIcon fontSize="small" />
        ) : value === "model-expert" ? (
          <PredictorIcon fontSize="small" />
        ) : (
          <LiteratureExpertIcon fontSize="small" color="info" />
        )
      }
      value={value}
      variant="filled"
    />
  );
}
